/**
 * Getting PadLab onto the Home Screen, and keeping it up to date once it is there.
 *
 * Three jobs live here because they are one user story:
 *
 * 1. **The install prompt**, where the browser gives us one (`beforeinstallprompt`, which is
 *    Chromium-only). Captured the moment it fires, because the event is single-shot and a
 *    browser that has already fired it will not fire it again on this page load.
 * 2. **The iOS guide**, where it does not. Safari has no programmatic install, so PadLab
 *    draws the Share-sheet steps itself. Shown once, in browser mode only, and never again
 *    after the app has been opened from the Home Screen or the guide has been dismissed —
 *    an install nag that reappears is the single most website-like thing a PWA can do.
 * 3. **The update handshake.** The service worker is registered here and, critically, is
 *    never allowed to activate on its own. A new build waits until the user taps "Install
 *    update" in an in-app affordance, and the button is not offered at all while a recording
 *    is running. Losing a take to a background code swap is unforgivable.
 *
 * The QR encoder further down is written out rather than imported: PadLab ships with zero
 * runtime dependencies, and a shareable install URL is only useful if somebody can point a
 * phone at it.
 */

import { computed, signal, type ReadonlySignal } from '../core/signal';
import { h, svg, mount, bindStyle, onCleanup } from '../core/dom';
import { createLogger, shape, tag } from '../core/log';
import {
  hasLaunchedInstalled,
  isStandalone,
  readStoredFlag,
  writeStoredFlag,
  clearStoredFlag,
} from './standalone';
import { copyText, installationURL } from './share';

const log = createLogger('install');

const FLAG_GUIDE_DISMISSED = 'install-guide-dismissed';

// MARK: - The browser's own install prompt

/** Not in lib.dom: `beforeinstallprompt` is a Chromium extension to the standard. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installAvailableSignal = signal(false);
const installedSignal = signal(hasLaunchedInstalled());

/** True when this browser will show a real install dialog if we ask it to. */
export const installAvailable: ReadonlySignal<boolean> = installAvailableSignal;

/** True once PadLab has been installed from this browser, or opened from the Home Screen. */
export const appInstalled: ReadonlySignal<boolean> = installedSignal;

window.addEventListener('beforeinstallprompt', (event) => {
  // Suppressing the browser's own mini-infobar is the whole point: PadLab decides where the
  // install affordance lives, so it can sit next to the QR code and the explanation instead
  // of covering the transport.
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  installAvailableSignal.value = true;
  log.debug('Install prompt captured');
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  installAvailableSignal.value = false;
  installedSignal.value = true;
  log.info('PadLab was installed');
});

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

/**
 * Shows the browser's install dialog. Must be called from a user gesture. The captured event
 * can only be used once, so a dismissal clears it — the browser will offer another one on a
 * later visit if it decides the user is engaged enough.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferredPrompt;
  if (event === null) return 'unavailable';
  deferredPrompt = null;
  installAvailableSignal.value = false;

  try {
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'accepted') installedSignal.value = true;
    return choice.outcome;
  } catch (error) {
    log.warn('Install prompt failed', { cause: shape(error) });
    return 'unavailable';
  }
}

// MARK: - iOS

export type InstallPlatform = 'prompt' | 'ios-safari' | 'ios-other-browser' | 'unsupported';

/**
 * iPadOS reports itself as a Mac, so the touch-point count is the only reliable separator.
 * Chrome, Firefox and Edge on iOS run on WebKit but do not offer Add to Home Screen, and
 * telling their users to look for a menu item that is not there is worse than saying nothing.
 */
function detectPlatform(): InstallPlatform {
  if ('onbeforeinstallprompt' in window) return 'prompt';

  const agent = navigator.userAgent;
  const touchMac = navigator.maxTouchPoints > 1 && /Macintosh/.test(agent);
  const iOS = /iPhone|iPad|iPod/.test(agent) || touchMac;
  if (!iOS) return 'unsupported';

  // WebKit-shell browsers announce themselves; anything else on iOS is Safari.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(agent)) return 'ios-other-browser';
  return 'ios-safari';
}

export const installPlatform: InstallPlatform = detectPlatform();

const guideDismissed = signal(readStoredFlag(FLAG_GUIDE_DISMISSED) === 'true');

/**
 * The one-time Add to Home Screen guide is offered only when every one of these is true:
 * we are in browser chrome, this browser has no install dialog of its own, the app has never
 * been opened from the Home Screen, and the guide has not already been dismissed.
 */
export const shouldShowInstallGuide: ReadonlySignal<boolean> = computed(
  () =>
    !isStandalone.value &&
    !installedSignal.value &&
    !guideDismissed.value &&
    installPlatform === 'ios-safari',
);

/** True when there is any install affordance worth showing at all. */
export const shouldOfferInstall: ReadonlySignal<boolean> = computed(
  () => !isStandalone.value && (installAvailableSignal.value || shouldShowInstallGuide.value),
);

export function dismissInstallGuide(): void {
  guideDismissed.value = true;
  writeStoredFlag(FLAG_GUIDE_DISMISSED, 'true');
}

/** The settings screen's "show me how to install this again" control. */
export function resetInstallGuide(): void {
  guideDismissed.value = false;
  clearStoredFlag(FLAG_GUIDE_DISMISSED);
}

// ---------------------------------------------------------------------------------------
// MARK: - QR encoder
//
// Byte mode, error-correction level M, versions 1 to 20 (up to 666 bytes — an install URL is
// never close to that). Written from ISO/IEC 18004 rather than pulled in as a dependency:
// PadLab has no runtime dependencies, and this is the whole encoder in about two hundred
// lines. Level M is chosen because a QR code on a screen or a printed flyer gets photographed
// at an angle in bad light; L would be smaller and noticeably less forgiving.
// ---------------------------------------------------------------------------------------

/** Per version (index = version − 1): EC codewords per block, then two groups of blocks. */
const LEVEL_M_BLOCKS: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
  [30, 1, 50, 4, 51],
  [22, 6, 36, 2, 37],
  [22, 8, 37, 1, 38],
  [24, 4, 40, 5, 41],
  [24, 5, 41, 5, 42],
  [28, 7, 45, 3, 46],
  [28, 10, 46, 1, 47],
  [26, 9, 43, 4, 44],
  [26, 3, 44, 11, 45],
  [26, 3, 41, 13, 42],
];

/** Bits appended after the interleaved codewords so the symbol fills exactly. */
function remainderBits(version: number): number {
  if (version === 1) return 0;
  if (version <= 6) return 7;
  if (version <= 13) return 0;
  return 3; // versions 14–20
}

// GF(256) with the QR primitive polynomial x^8 + x^4 + x^3 + x^2 + 1.
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let value = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = value;
    GF_LOG[value] = i;
    value <<= 1;
    if ((value & 0x100) !== 0) value ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255] as number;
}

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[(GF_LOG[a] as number) + (GF_LOG[b] as number)] as number;
}

/** g(x) = ∏(x − α^i), coefficients highest degree first. */
function generatorPolynomial(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const root = GF_EXP[i] as number;
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      const coefficient = poly[j] as number;
      next[j] = (next[j] as number) ^ coefficient;
      next[j + 1] = (next[j + 1] as number) ^ gfMultiply(coefficient, root);
    }
    poly = next;
  }
  return poly;
}

function errorCorrection(data: Uint8Array, count: number): Uint8Array {
  const generator = generatorPolynomial(count);
  const buffer = new Uint8Array(data.length + count);
  buffer.set(data);
  for (let i = 0; i < data.length; i++) {
    const factor = buffer[i] as number;
    if (factor === 0) continue;
    for (let j = 0; j < generator.length; j++) {
      buffer[i + j] = (buffer[i + j] as number) ^ gfMultiply(generator[j] as number, factor);
    }
  }
  return buffer.slice(data.length);
}

function characterCountBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

function dataCodewordCount(version: number): number {
  const spec = LEVEL_M_BLOCKS[version - 1];
  if (spec === undefined) throw new Error(`QR version ${version} is outside the supported range.`);
  const [, group1Blocks, group1Data, group2Blocks, group2Data] = spec;
  return group1Blocks * group1Data + group2Blocks * group2Data;
}

function byteCapacity(version: number): number {
  const bits = dataCodewordCount(version) * 8 - 4 - characterCountBits(version);
  return Math.floor(bits / 8);
}

function chooseVersion(byteLength: number): number {
  for (let version = 1; version <= LEVEL_M_BLOCKS.length; version++) {
    if (byteLength <= byteCapacity(version)) return version;
  }
  throw new Error('That URL is too long to encode as a QR code at error-correction level M.');
}

/** Mode indicator, length, payload, terminator and pad bytes — one full data block stream. */
function buildDataCodewords(data: Uint8Array, version: number): Uint8Array {
  const total = dataCodewordCount(version);
  const out = new Uint8Array(total);
  let bitPosition = 0;

  const put = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) {
      const bit = (value >>> i) & 1;
      if (bit === 1) {
        const index = bitPosition >>> 3;
        out[index] = (out[index] as number) | (0x80 >>> (bitPosition & 7));
      }
      bitPosition++;
    }
  };

  put(0b0100, 4); // byte mode
  put(data.length, characterCountBits(version));
  for (const byte of data) put(byte, 8);

  // Terminator: up to four zero bits, then zero-fill to the next byte boundary. Both are
  // already zero in a fresh buffer, so only the position moves.
  const capacityBits = total * 8;
  bitPosition = Math.min(bitPosition + 4, capacityBits);
  bitPosition = Math.ceil(bitPosition / 8) * 8;

  // Alternating pad bytes, per the standard.
  let padIndex = 0;
  for (let index = bitPosition >>> 3; index < total; index++) {
    out[index] = padIndex % 2 === 0 ? 0xec : 0x11;
    padIndex++;
  }

  return out;
}

/** Splits into blocks, adds ECC, and interleaves both halves as the standard requires. */
function interleave(codewords: Uint8Array, version: number): Uint8Array {
  const spec = LEVEL_M_BLOCKS[version - 1] as readonly [number, number, number, number, number];
  const [ecPerBlock, group1Blocks, group1Data, group2Blocks, group2Data] = spec;

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;

  const pushBlock = (length: number): void => {
    const block = codewords.slice(offset, offset + length);
    offset += length;
    dataBlocks.push(block);
    ecBlocks.push(errorCorrection(block, ecPerBlock));
  };

  for (let i = 0; i < group1Blocks; i++) pushBlock(group1Data);
  for (let i = 0; i < group2Blocks; i++) pushBlock(group2Data);

  const result = new Uint8Array(codewords.length + ecPerBlock * dataBlocks.length);
  let cursor = 0;

  const longest = Math.max(group1Data, group2Data);
  for (let i = 0; i < longest; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) result[cursor++] = block[i] as number;
    }
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) result[cursor++] = block[i] as number;
  }

  return result;
}

/** A finished symbol. `at(x, y)` is true where the module is dark. */
export interface QRSymbol {
  readonly size: number;
  at(x: number, y: number): boolean;
}

class SymbolGrid {
  readonly size: number;
  private readonly dark: Uint8Array;
  private readonly functional: Uint8Array;

  constructor(size: number) {
    this.size = size;
    this.dark = new Uint8Array(size * size);
    this.functional = new Uint8Array(size * size);
  }

  private index(x: number, y: number): number {
    return y * this.size + x;
  }

  inside(x: number, y: number): boolean {
    return x >= 0 && x < this.size && y >= 0 && y < this.size;
  }

  at(x: number, y: number): boolean {
    return this.dark[this.index(x, y)] === 1;
  }

  isFunctional(x: number, y: number): boolean {
    return this.functional[this.index(x, y)] === 1;
  }

  set(x: number, y: number, dark: boolean): void {
    this.dark[this.index(x, y)] = dark ? 1 : 0;
  }

  setFunction(x: number, y: number, dark: boolean): void {
    if (!this.inside(x, y)) return;
    const index = this.index(x, y);
    this.dark[index] = dark ? 1 : 0;
    this.functional[index] = 1;
  }

  flip(x: number, y: number): void {
    const index = this.index(x, y);
    this.dark[index] = this.dark[index] === 1 ? 0 : 1;
  }
}

function alignmentPositions(version: number, size: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step = Math.floor((version * 4 + count * 2 + 1) / (count * 2 - 2)) * 2;
  const positions = new Array<number>(count).fill(6);
  let position = size - 7;
  for (let i = count - 1; i >= 1; i--) {
    positions[i] = position;
    position -= step;
  }
  return positions;
}

function drawFunctionPatterns(grid: SymbolGrid, version: number): void {
  const size = grid.size;

  for (let i = 0; i < size; i++) {
    grid.setFunction(6, i, i % 2 === 0);
    grid.setFunction(i, 6, i % 2 === 0);
  }

  const finder = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        grid.setFunction(cx + dx, cy + dy, distance !== 2 && distance !== 4);
      }
    }
  };
  finder(3, 3);
  finder(size - 4, 3);
  finder(3, size - 4);

  const positions = alignmentPositions(version, size);
  const last = positions.length - 1;
  for (let i = 0; i < positions.length; i++) {
    for (let j = 0; j < positions.length; j++) {
      // The three that would sit on a finder pattern are omitted.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue;
      const cx = positions[i] as number;
      const cy = positions[j] as number;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          grid.setFunction(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  drawVersionInformation(grid, version);
}

function drawVersionInformation(grid: SymbolGrid, version: number): void {
  if (version < 7) return;
  let remainder = version;
  for (let i = 0; i < 12; i++) {
    remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
  }
  const bits = (version << 12) | remainder;
  const size = grid.size;
  for (let i = 0; i < 18; i++) {
    const bit = ((bits >>> i) & 1) === 1;
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    grid.setFunction(a, b, bit);
    grid.setFunction(b, a, bit);
  }
}

/** Level M's own two bits are `00`; the mask number completes the five data bits. */
function drawFormatInformation(grid: SymbolGrid, mask: number): void {
  const data = (0b00 << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i++) {
    remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  }
  const bits = ((data << 10) | remainder) ^ 0x5412;
  const size = grid.size;
  const bit = (index: number): boolean => ((bits >>> index) & 1) === 1;

  for (let i = 0; i <= 5; i++) grid.setFunction(8, i, bit(i));
  grid.setFunction(8, 7, bit(6));
  grid.setFunction(8, 8, bit(7));
  grid.setFunction(7, 8, bit(8));
  for (let i = 9; i < 15; i++) grid.setFunction(14 - i, 8, bit(i));

  for (let i = 0; i < 8; i++) grid.setFunction(size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) grid.setFunction(8, size - 15 + i, bit(i));

  // The module that is dark in every valid symbol.
  grid.setFunction(8, size - 8, true);
}

function placeCodewords(grid: SymbolGrid, codewords: Uint8Array): void {
  const size = grid.size;
  let bit = 0;
  const total = codewords.length * 8;

  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern; the pairing steps around it.
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < size; vertical++) {
      for (let column = 0; column < 2; column++) {
        const x = right - column;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vertical : vertical;
        if (grid.isFunctional(x, y) || bit >= total) continue;
        const byte = codewords[bit >>> 3] as number;
        grid.set(x, y, ((byte >>> (7 - (bit & 7))) & 1) === 1);
        bit++;
      }
    }
  }
}

function maskCondition(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

function applyMask(grid: SymbolGrid, mask: number): void {
  for (let y = 0; y < grid.size; y++) {
    for (let x = 0; x < grid.size; x++) {
      if (grid.isFunctional(x, y)) continue;
      if (maskCondition(mask, x, y)) grid.flip(x, y);
    }
  }
}

/** The 1:1:3:1:1 finder lookalike, and its mirror, that rule 3 penalises. */
const FINDER_LIKE = [
  [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1],
];

function matchesFinderLike(line: number[], start: number): boolean {
  for (const pattern of FINDER_LIKE) {
    let hit = true;
    for (let i = 0; i < pattern.length; i++) {
      if (line[start + i] !== pattern[i]) {
        hit = false;
        break;
      }
    }
    if (hit) return true;
  }
  return false;
}

function penalty(grid: SymbolGrid): number {
  const size = grid.size;
  let score = 0;

  const lines: number[][] = [];
  for (let y = 0; y < size; y++) {
    const row: number[] = [];
    for (let x = 0; x < size; x++) row.push(grid.at(x, y) ? 1 : 0);
    lines.push(row);
  }
  for (let x = 0; x < size; x++) {
    const column: number[] = [];
    for (let y = 0; y < size; y++) column.push(grid.at(x, y) ? 1 : 0);
    lines.push(column);
  }

  // Rule 1: runs of five or more.
  for (const line of lines) {
    let run = 1;
    for (let i = 1; i < line.length; i++) {
      if (line[i] === line[i - 1]) {
        run++;
        continue;
      }
      if (run >= 5) score += 3 + (run - 5);
      run = 1;
    }
    if (run >= 5) score += 3 + (run - 5);
  }

  // Rule 2: solid 2×2 blocks.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const first = grid.at(x, y);
      if (
        first === grid.at(x + 1, y) &&
        first === grid.at(x, y + 1) &&
        first === grid.at(x + 1, y + 1)
      ) {
        score += 3;
      }
    }
  }

  // Rule 3: finder-like patterns. The quiet zone counts as light, so each line is padded.
  for (const line of lines) {
    const padded = [0, 0, 0, 0, ...line, 0, 0, 0, 0];
    for (let i = 0; i + 11 <= padded.length; i++) {
      if (matchesFinderLike(padded, i)) score += 40;
    }
  }

  // Rule 4: distance from an even balance of dark and light.
  let dark = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) if (grid.at(x, y)) dark++;
  }
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/**
 * Encodes `text` as a QR symbol at error-correction level M, choosing the smallest version
 * that fits and the mask with the lowest penalty score.
 */
export function encodeQR(text: string): QRSymbol {
  const data = new TextEncoder().encode(text);
  const version = chooseVersion(data.length);
  const size = version * 4 + 17;

  const codewords = interleave(buildDataCodewords(data, version), version);
  const padded = new Uint8Array(codewords.length + Math.ceil(remainderBits(version) / 8));
  padded.set(codewords);

  let best: SymbolGrid | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask++) {
    const grid = new SymbolGrid(size);
    drawFunctionPatterns(grid, version);
    drawFormatInformation(grid, mask);
    placeCodewords(grid, padded);
    applyMask(grid, mask);
    const score = penalty(grid);
    if (score < bestScore) {
      bestScore = score;
      best = grid;
    }
  }

  const chosen = best as SymbolGrid;
  return { size: chosen.size, at: (x, y) => chosen.at(x, y) };
}

export interface QRRenderOptions {
  /** Rendered edge length in CSS pixels, quiet zone included. */
  readonly size?: number;
  /** Quiet-zone width in modules. The standard requires four; less breaks some scanners. */
  readonly margin?: number;
}

/**
 * Draws the symbol on a canvas at device resolution.
 *
 * Dark modules on a light field, always — an inverted QR code is unreadable to a large share
 * of scanners, so this one tile stays light even though every other surface in PadLab is
 * near-black. It is drawn as a deliberate white card rather than apologised for.
 */
export function renderQRCanvas(text: string, options: QRRenderOptions = {}): HTMLCanvasElement {
  const symbol = encodeQR(text);
  const margin = options.margin ?? 4;
  const cssSize = options.size ?? 208;
  const ratio = Math.min(window.devicePixelRatio || 1, 3);
  const modules = symbol.size + margin * 2;

  // Integer module size keeps every edge on a device pixel; a fractional one produces the
  // grey seams that make a code fail to scan at small sizes.
  const modulePixels = Math.max(1, Math.floor((cssSize * ratio) / modules));
  const pixels = modulePixels * modules;

  const canvas = document.createElement('canvas');
  canvas.width = pixels;
  canvas.height = pixels;
  canvas.style.setProperty('width', `${pixels / ratio}px`);
  canvas.style.setProperty('height', `${pixels / ratio}px`);
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', `QR code linking to ${text}`);

  const context = canvas.getContext('2d');
  if (context === null) return canvas;

  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, pixels, pixels);
  context.fillStyle = '#0A0A0C';
  for (let y = 0; y < symbol.size; y++) {
    for (let x = 0; x < symbol.size; x++) {
      if (!symbol.at(x, y)) continue;
      context.fillRect(
        (x + margin) * modulePixels,
        (y + margin) * modulePixels,
        modulePixels,
        modulePixels,
      );
    }
  }

  return canvas;
}

// ---------------------------------------------------------------------------------------
// MARK: - Service worker registration and the update handshake
// ---------------------------------------------------------------------------------------

const updateReadySignal = signal(false);
const workerVersionSignal = signal<string | null>(null);
const recordingActiveSignal = signal(false);

/** True when a new build is installed and waiting for the user's permission to take over. */
export const updateReady: ReadonlySignal<boolean> = updateReadySignal;

/** The running worker's version string, once it has answered GET_VERSION. */
export const serviceWorkerVersion: ReadonlySignal<string | null> = workerVersionSignal;

/**
 * The update button is hidden — not disabled — while a take is running. A disabled control
 * invites the user to wait and watch it; a hidden one lets them finish the take.
 */
export const updateOfferable: ReadonlySignal<boolean> = computed(
  () => updateReadySignal.value && !recordingActiveSignal.value,
);

let registration: ServiceWorkerRegistration | null = null;
let reloading = false;
let lastUpdateCheck = 0;

/** Re-checking more often than this on foreground is just noise on a metered connection. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

function ask(worker: ServiceWorker, message: unknown, timeoutMs = 3000): Promise<unknown> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => {
      channel.port1.close();
      resolve(null);
    }, timeoutMs);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(event.data);
    };
    worker.postMessage(message, [channel.port2]);
  });
}

function watchInstalling(worker: ServiceWorker): void {
  worker.addEventListener('statechange', () => {
    // `controller` is null on the very first install; that is not an update, it is the app
    // becoming offline-capable for the first time and needs no prompt.
    if (worker.state === 'installed' && navigator.serviceWorker.controller !== null) {
      updateReadySignal.value = true;
      log.info('A new PadLab build is installed and waiting');
    }
  });
}

/**
 * Registers the worker and wires the update handshake.
 *
 * Registered as a **classic** script deliberately: module service workers need Safari 16.4
 * and PadLab is not prepared to drop offline support on older WebKit to gain nothing.
 * Calling this more than once, or alongside a registration made elsewhere, is safe — the
 * browser returns the existing registration for the same URL.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // Registration is idempotent, but the listeners attached below are not.
  if (registration !== null) return registration;

  try {
    registration = await navigator.serviceWorker.register('./sw.js');
  } catch (error) {
    log.warn('Service worker registration failed; PadLab will run online only', {
      cause: shape(error),
    });
    return null;
  }

  if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
    updateReadySignal.value = true;
  }
  if (registration.installing !== null) watchInstalling(registration.installing);

  registration.addEventListener('updatefound', () => {
    const installing = registration?.installing;
    if (installing !== null && installing !== undefined) watchInstalling(installing);
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only reload for an update the user asked for. A `controllerchange` from the first
    // install's `clients.claim()` must not throw away whatever is on screen.
    if (!reloading) return;
    window.location.reload();
  });

  const controller = navigator.serviceWorker.controller;
  if (controller !== null) {
    const reply = (await ask(controller, { type: 'GET_VERSION' })) as { version?: unknown } | null;
    if (reply !== null && typeof reply.version === 'string') {
      workerVersionSignal.value = reply.version;
    }
  }

  // Coming back to the foreground is the natural moment to look for a new build: the user is
  // about to start working, and an update found now can be offered before they do.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });

  return registration;
}

/** Asks the browser to re-fetch sw.js. Throttled; safe to call from anywhere. */
export async function checkForUpdate(force = false): Promise<void> {
  if (registration === null) return;
  const now = Date.now();
  if (!force && now - lastUpdateCheck < UPDATE_CHECK_INTERVAL_MS) return;
  lastUpdateCheck = now;
  await registration.update().catch((error: unknown) => {
    log.debug('Update check failed', { cause: shape(error) });
  });
  // Re-offers an update the user pushed away with "Later". `updatefound` will not fire a
  // second time for a build that is already waiting, so without this the deferral would be
  // permanent for the life of the tab.
  if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
    updateReadySignal.value = true;
  }
}

/**
 * Tells the audio engine's recording state to the update flow and to the worker.
 *
 * The page is the authority here — it is the only thing that knows a take is running — and
 * the worker keeps its own copy purely so that a stray SKIP_WAITING cannot activate a build
 * underneath a live microphone.
 */
export function setRecordingActive(active: boolean): void {
  if (recordingActiveSignal.peek() === active) return;
  recordingActiveSignal.value = active;
  const controller = navigator.serviceWorker?.controller ?? null;
  if (controller !== null) controller.postMessage({ type: 'RECORDING_STATE', active });
}

export type UpdateOutcome = 'applied' | 'blocked-by-recording' | 'nothing-waiting' | 'failed';

/**
 * Applies a waiting update. Reloads the page on success, which is the only way to run the
 * new bundle — so this is never called except from an explicit user action.
 */
export async function applyUpdate(): Promise<UpdateOutcome> {
  if (recordingActiveSignal.peek()) return 'blocked-by-recording';

  const waiting = registration?.waiting ?? null;
  if (waiting === null) {
    updateReadySignal.value = false;
    return 'nothing-waiting';
  }

  let reply: { type?: unknown } | null;
  try {
    reply = (await ask(waiting, { type: 'SKIP_WAITING' })) as { type?: unknown } | null;
  } catch (error) {
    // postMessage throws if the waiting worker became redundant between the check and now.
    log.warn('The waiting worker could not be reached', { cause: shape(error) });
    return 'failed';
  }
  if (reply !== null && reply.type === 'SKIP_WAITING_REFUSED') return 'blocked-by-recording';

  reloading = true;
  // `controllerchange` normally arrives within a frame or two. The timeout is the escape
  // hatch for a browser that activates without firing it, so the user is never left looking
  // at a button that did nothing.
  window.setTimeout(() => {
    if (reloading) window.location.reload();
  }, 2500);
  return 'applied';
}

// ---------------------------------------------------------------------------------------
// MARK: - Interface
// ---------------------------------------------------------------------------------------

const CARD_STYLE = {
  border: 'var(--hairline-width) solid var(--hairline)',
  borderRadius: 'var(--radius-card)',
  background: 'var(--ink-raised)',
  padding: 'var(--space-6)',
} as const;

function kicker(text: string): HTMLElement {
  return h('p', { class: 'kicker' }, text);
}

function primaryButton(label: string, onPress: () => void): HTMLButtonElement {
  return h(
    'button',
    {
      type: 'button',
      class: 'install-action',
      onclick: onPress,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        // Stated here as well as in the `button` reset, so the sheet is still thumb-sized if
        // it is ever mounted somewhere that has not loaded tokens.css.
        minHeight: 'var(--touch-min, 44px)',
        padding: '0 var(--space-7)',
        borderRadius: 'var(--radius-control)',
        background: 'var(--accent-magenta)',
        color: 'var(--on-accent-dark)',
        fontSize: 'var(--type-headline)',
        fontWeight: 'var(--weight-semibold)',
      },
    },
    label,
  );
}

function quietButton(label: string, onPress: () => void): HTMLButtonElement {
  return h(
    'button',
    {
      type: 'button',
      onclick: onPress,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 'var(--touch-min, 44px)',
        padding: '0 var(--space-6)',
        borderRadius: 'var(--radius-control)',
        border: 'var(--hairline-width) solid var(--hairline)',
        color: 'var(--tone-secondary)',
        fontSize: 'var(--type-callout)',
        fontWeight: 'var(--weight-medium)',
      },
    },
    label,
  );
}

/** iOS's Share glyph: a rounded box with an arrow rising out of it. */
function shareGlyph(size = 22): SVGElement {
  return svg(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: String(size),
      height: String(size),
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.8',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    svg('path', { d: 'M12 3.2v11' }),
    svg('path', { d: 'M8.4 6.8 12 3.2l3.6 3.6' }),
    svg('path', { d: 'M7.2 10.4H6a1.8 1.8 0 0 0-1.8 1.8v6.6A1.8 1.8 0 0 0 6 20.6h12a1.8 1.8 0 0 0 1.8-1.8v-6.6a1.8 1.8 0 0 0-1.8-1.8h-1.2' }),
  );
}

/** The "Add to Home Screen" row glyph: a plus inside a rounded square. */
function addToHomeGlyph(size = 20): SVGElement {
  return svg(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: String(size),
      height: String(size),
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.8',
      'stroke-linecap': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    svg('rect', { x: '3.4', y: '3.4', width: '17.2', height: '17.2', rx: '4.6' }),
    svg('path', { d: 'M12 8.4v7.2M8.4 12h7.2' }),
  );
}

/**
 * The Share-sheet illustration.
 *
 * Drawn rather than screenshotted: a screenshot of somebody else's operating system dates
 * the moment iOS restyles a sheet, and cannot be recoloured for PadLab's palette. This shows
 * the shape of the sheet and the row to look for, which is the whole information content.
 */
function shareSheetIllustration(): HTMLElement {
  const row = (label: string, glyph: SVGElement, highlighted: boolean): HTMLElement =>
    h(
      'li',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-5)',
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-chip)',
          // Emphasis is a full border plus a faint tint — never a bar on the leading edge.
          border: highlighted
            ? 'var(--hairline-width) solid color-mix(in srgb, var(--accent-magenta) 46%, var(--hairline))'
            : 'var(--hairline-width) solid transparent',
          background: highlighted
            ? 'color-mix(in srgb, var(--accent-magenta) 10%, transparent)'
            : 'transparent',
          color: highlighted ? 'var(--tone-primary)' : 'var(--tone-tertiary)',
          fontSize: 'var(--type-callout)',
          fontWeight: highlighted ? 'var(--weight-semibold)' : 'var(--weight-regular)',
        },
      },
      h('span', { style: { display: 'flex', flex: 'none' } }, glyph),
      label,
    );

  const dimmedGlyph = (): SVGElement =>
    svg(
      'svg',
      {
        viewBox: '0 0 24 24',
        width: '20',
        height: '20',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '1.8',
        'stroke-linecap': 'round',
        'aria-hidden': 'true',
        focusable: 'false',
      },
      svg('rect', { x: '3.4', y: '3.4', width: '17.2', height: '17.2', rx: '4.6' }),
      svg('path', { d: 'M8 12h8' }),
    );

  return h(
    'figure',
    {
      style: {
        border: 'var(--hairline-width) solid var(--hairline)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--ink-surface)',
        padding: 'var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
      },
    },
    // The Safari toolbar, with the Share control called out.
    h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-5)',
          borderRadius: 'var(--radius-chip)',
          background: 'var(--ink-base)',
          border: 'var(--hairline-width) solid var(--hairline)',
          color: 'var(--tone-tertiary)',
        },
      },
      h('span', { 'aria-hidden': 'true', style: { fontSize: 'var(--type-body)' } }, '‹'),
      h('span', { 'aria-hidden': 'true', style: { fontSize: 'var(--type-body)' } }, '›'),
      h(
        'span',
        {
          style: {
            display: 'flex',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-chip)',
            // The acid stays on the glyph, which is where colour is allowed and where it still
            // marks the Share control. It came off the border and the fill: an accent edge
            // around an accent-tinted box is the pattern this app never ships. Nothing here
            // renders today, so this is a landmine defused rather than a bug fixed.
            color: 'var(--accent-acid)',
            border: 'var(--hairline-width) solid var(--hairline-strong)',
            background: 'var(--ink-raised)',
          },
        },
        shareGlyph(),
      ),
      h('span', { 'aria-hidden': 'true', style: { fontSize: 'var(--type-body)' } }, '▤'),
      h('span', { 'aria-hidden': 'true', style: { fontSize: 'var(--type-body)' } }, '⧉'),
    ),
    // The sheet it opens.
    h(
      'ul',
      { style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' } },
      row('Add to Reading List', dimmedGlyph(), false),
      row('Add to Home Screen', addToHomeGlyph(), true),
      row('Markup', dimmedGlyph(), false),
    ),
    h(
      'figcaption',
      { class: 'kicker', style: { textAlign: 'center' } },
      'Safari: Share, then Add to Home Screen',
    ),
  );
}

function stepList(steps: readonly string[]): HTMLElement {
  return h(
    'ol',
    { style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' } },
    ...steps.map((text, index) =>
      h(
        'li',
        {
          style: {
            display: 'flex',
            gap: 'var(--space-5)',
            alignItems: 'flex-start',
            fontSize: 'var(--type-body)',
            color: 'var(--tone-secondary)',
          },
        },
        h(
          'span',
          {
            'aria-hidden': 'true',
            class: 'numeric',
            style: {
              flex: 'none',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: 'var(--hairline-width) solid var(--hairline-strong)',
              color: 'var(--tone-primary)',
              fontSize: 'var(--type-caption)',
            },
          },
          String(index + 1),
        ),
        h('span', null, text),
      ),
    ),
  );
}

const IOS_SAFARI_STEPS = [
  'Tap the Share button in the Safari toolbar: the square with an arrow coming out of it.',
  'Scroll down the list of actions and choose Add to Home Screen.',
  'Tap Add. PadLab opens full-screen from your Home Screen, works offline, and keeps your projects on the device.',
] as const;

const IOS_OTHER_BROWSER_STEPS = [
  'Open this page in Safari. Only Safari can add a web app to the iPhone Home Screen.',
  'Tap the Share button, then choose Add to Home Screen.',
  'Tap Add. PadLab opens full-screen, works offline, and keeps your projects on the device.',
] as const;

export interface InstallSheetOptions {
  /** Called after the user dismisses the guide, so the host can close its container. */
  readonly onDismiss?: () => void;
  /** Set false on a dedicated install screen where the QR would be redundant. */
  readonly showQRCode?: boolean;
}

/**
 * The complete install affordance: a real button where the browser gives us one, the Safari
 * steps where it does not, and in both cases a link plus a QR code so the page can be opened
 * on the phone it is meant to live on.
 */
export function createInstallSheet(options: InstallSheetOptions = {}): HTMLElement {
  const url = installationURL();
  const showQR = options.showQRCode ?? true;
  const status = h(
    'p',
    {
      role: 'status',
      'aria-live': 'polite',
      style: {
        minHeight: '1.2em',
        fontSize: 'var(--type-caption)',
        color: 'var(--tone-secondary)',
      },
    },
    '',
  );

  const say = (message: string): void => {
    status.textContent = message;
  };

  const action = primaryButton('Install PadLab', () => {
    void promptInstall().then((outcome) => {
      if (outcome === 'accepted') say('Installing. PadLab will appear with your other apps.');
      else if (outcome === 'dismissed') say('No problem. You can install it later from here.');
      else say('This browser did not offer an install dialog. Use the steps above instead.');
    });
  });
  // `beforeinstallprompt` can fire after this sheet is built, so the button is bound rather
  // than decided once. Nothing else in the sheet changes with it.
  onCleanup(
    action,
    bindStyle(
      action,
      'display',
      computed(() => (installAvailable.value ? 'inline-flex' : 'none')),
    ),
  );

  const steps =
    installPlatform === 'ios-other-browser'
      ? IOS_OTHER_BROWSER_STEPS
      : installPlatform === 'ios-safari'
        ? IOS_SAFARI_STEPS
        : null;

  const qr = showQR ? buildQRBlock(url, say) : null;

  return h(
    'section',
    {
      class: 'install-sheet',
      'aria-labelledby': 'install-sheet-title',
      style: { ...CARD_STYLE, display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' },
    },
    kicker('Add to Home Screen'),
    h(
      'h2',
      {
        id: 'install-sheet-title',
        style: {
          fontSize: 'var(--type-title)',
          fontWeight: 'var(--weight-semibold)',
          lineHeight: 'var(--leading-tight)',
        },
      },
      'Install PadLab and it stops being a website',
    ),
    h(
      'p',
      { style: { fontSize: 'var(--type-body)', color: 'var(--tone-secondary)' } },
      'Installed, PadLab runs full-screen with no browser bar, launches offline, holds the microphone for Voice Lab, and keeps your projects in storage the browser will not quietly reclaim.',
    ),
    steps === null ? null : shareSheetIllustration(),
    steps === null ? null : stepList(steps),
    action,
    qr,
    status,
    h(
      'div',
      { style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' } },
      quietButton('Copy link', () => {
        void copyText(url).then((ok) => {
          say(ok ? 'Link copied.' : `Copy this address: ${url}`);
        });
      }),
      quietButton('Not now', () => {
        dismissInstallGuide();
        options.onDismiss?.();
      }),
    ),
  );
}

function buildQRBlock(url: string, say: (message: string) => void): HTMLElement | null {
  let canvas: HTMLCanvasElement;
  try {
    canvas = renderQRCanvas(url, { size: 160 });
  } catch (error) {
    // A URL long enough to overflow version 20 is not a reason to lose the whole sheet.
    log.warn('QR code could not be generated', { cause: shape(error), url: tag('install-url') });
    say(`Open this address on your phone: ${url}`);
    return null;
  }

  return h(
    'div',
    {
      style: {
        display: 'flex',
        alignItems: 'center',
        // Wraps to a stacked layout on a phone rather than squeezing the copy into a column
        // three words wide. `flex-wrap` does this without a media query, which matters
        // because these styles are written through the CSSOM and cannot carry one.
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 'var(--space-6)',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-card)',
        border: 'var(--hairline-width) solid var(--hairline)',
        background: 'var(--ink-surface)',
      },
    },
    // The one light surface in PadLab. A scanner needs dark modules on a light field, so the
    // code is presented as a deliberate white card rather than inverted into the theme.
    h(
      'div',
      {
        style: {
          flex: 'none',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-control)',
          background: '#ffffff',
          lineHeight: '0',
        },
      },
      canvas,
    ),
    h(
      'div',
      {
        style: {
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          flex: '1 1 13rem',
          minWidth: '0',
        },
      },
      kicker('Open on your phone'),
      h(
        'p',
        { style: { fontSize: 'var(--type-caption)', color: 'var(--tone-secondary)' } },
        'Point the Camera app at this code to open PadLab on the device you want to install it on.',
      ),
      h(
        'p',
        {
          class: 'selectable',
          style: {
            fontSize: 'var(--type-caption)',
            color: 'var(--tone-tertiary)',
            overflowWrap: 'anywhere',
          },
        },
        url,
      ),
    ),
  );
}

/**
 * The in-app "Update ready" affordance.
 *
 * Deliberately not a modal and not an auto-reload: a new build arriving mid-session is not an
 * emergency, and interrupting a performance to announce one would be the worst possible time.
 * The banner mounts itself into `host` only while an update is both waiting and safe to
 * apply, and removes itself while a recording is running. Returns a disposer.
 */
export function mountUpdateBanner(host: HTMLElement): () => void {
  let attached: (() => void) | null = null;

  const build = (): HTMLElement => {
    const status = h(
      'p',
      {
        role: 'status',
        'aria-live': 'polite',
        style: { fontSize: 'var(--type-caption)', color: 'var(--tone-secondary)' },
      },
      'A new version of PadLab is downloaded and ready. Nothing changes until you install it.',
    );

    return h(
      'div',
      {
        class: 'update-banner',
        style: {
          ...CARD_STYLE,
          // One neutral hairline, and the `background` override is deleted rather than
          // replaced: CARD_STYLE already supplies `var(--ink-raised)`. What is left is exactly
          // `.callout--accent` from tokens.css, and the meaning is carried by the word in
          // `kicker('Update ready')` below, which is where meaning belongs.
          borderColor: 'var(--hairline-strong)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        },
      },
      kicker('Update ready'),
      status,
      h(
        'div',
        { style: { display: 'flex', gap: 'var(--space-4)' } },
        primaryButton('Install update', () => {
          void applyUpdate().then((outcome) => {
            if (outcome === 'blocked-by-recording') {
              status.textContent =
                'A recording is running. PadLab will not update until the take is finished.';
            } else if (outcome === 'nothing-waiting') {
              status.textContent = 'That update has already been installed.';
            } else if (outcome === 'failed') {
              status.textContent = 'The update could not be applied. Try again in a moment.';
            }
          });
        }),
        quietButton('Later', () => {
          updateReadySignal.value = false;
        }),
      ),
    );
  };

  const sync = (): void => {
    const shouldShow = updateOfferable.peek();
    if (shouldShow && attached === null) attached = mount(host, build());
    else if (!shouldShow && attached !== null) {
      attached();
      attached = null;
    }
  };

  const unsubscribe = updateOfferable.subscribe(sync);
  return () => {
    unsubscribe();
    attached?.();
    attached = null;
  };
}
